import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Astro Vastu Remedies - Pinnacle Vastu';
const SITE_URL = 'https://astrovasturemedies.in';
const DEFAULT_IMAGE = 'https://astrovasturemedies.in/og-image.jpg';
const TWITTER_HANDLE = '@astrovastu';
const AUTHOR = 'Astro Vastu Remedies Team';

// Social crawlers (WhatsApp/Facebook/Instagram) require ABSOLUTE image URLs.
// Product images are stored as relative object-storage proxy paths (e.g. /api/media/file/..),
// so we prefix them with the production origin.
export const toAbsoluteUrl = (url) => {
  if (!url) return DEFAULT_IMAGE;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Primary SEO Keywords
const PRIMARY_KEYWORDS = 'astro vastu, astro vastu remedies, astro vastu consultation, astro vastu expert, astro vastu analysis, astrology vastu remedies, vastu astrology remedies, astro vastu for home, astro vastu for business, astro vastu solutions, astro vastu correction';

// Problem-based Keywords (high conversion)
const PROBLEM_KEYWORDS = 'vastu remedies for money problems, vastu remedies for business loss, vastu remedies for career growth, vastu remedies for health problems, vastu remedies for relationship issues, vastu remedies for financial growth, vastu remedies for success, vastu remedies for job problems';

// Local SEO Keywords
const LOCAL_KEYWORDS = 'vastu consultant india, vastu expert india, online vastu consultation india, best vastu consultant india, top vastu expert india, best astro vastu consultant in india';

// Brand Keywords
const BRAND_KEYWORDS = 'pinnacle vastu, pinnacle vastu remedies, astro vastu course';

// Default SEO configuration - Astro Vastu Focused
const defaultSEO = {
  title: 'Astro Vastu Remedies | Online Astro Vastu Consultation | Astro Vastu Remedies',
  description: 'Get powerful Astro Vastu remedies based on astrology and vastu principles. Solve problems related to money, health, career and relationships with expert consultation by Astro Vastu Remedies.',
  keywords: `${PRIMARY_KEYWORDS}, ${PROBLEM_KEYWORDS}, ${LOCAL_KEYWORDS}, ${BRAND_KEYWORDS}`,
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: 'Astro Vastu Remedies | Best Astro Vastu Consultant in India | Astro Vastu Remedies',
    description: 'Get powerful Astro Vastu remedies for home, business, career problems. Expert consultation by Astro Vastu Remedies. Solve money, health, relationship issues with Pinnacle Vastu solutions.',
    keywords: `${PRIMARY_KEYWORDS}, ${PROBLEM_KEYWORDS}, vastu dosh remedies, instant vastu remedies, simple vastu remedies, vastu remedies without demolition, kundli based vastu remedies, planet based vastu remedies`,
  },
  products: {
    title: 'Astro Vastu Products | Vastu Remedies & Correction Products | Astro Vastu Remedies',
    description: 'Shop authentic Astro Vastu remedy products - yantras, gemstones, rudraksha, pyramids for vastu correction. All products energized by Astro Vastu Remedies. Vastu solutions without demolition.',
    keywords: 'astro vastu products, vastu correction products, vastu remedies products, yantra for vastu, gemstone for vastu, vastu pyramid, vastu dosh nivaran products, pinnacle vastu products',
  },
  combos: {
    title: 'Spiritual Combo Deals | Curated Vastu & Astrology Bundles | Astro Vastu Remedies',
    description: 'Shop hand-picked spiritual combo deals for wealth, health, protection and love. Save more when you buy bracelets, gemstones, rudraksha, yantras and crystal trees together. Energized by expert astrologers.',
    keywords: 'spiritual combo deals, vastu combo, astrology product bundles, wealth combo, protection combo, health combo, love combo, gemstone rudraksha bundle',
  },
  kundli: {
    title: 'Free Kundli | Kundli Based Vastu Remedies | Astro Vastu Consultation',
    description: 'Generate free Kundli and get personalized Astro Vastu remedies based on your birth chart. Planet-based vastu corrections for home and business by expert astrologer.',
    keywords: 'free kundli, kundli based vastu remedies, planet based vastu remedies, astro vastu consultation, birth chart vastu, horoscope vastu remedies, kundli analysis for vastu',
  },
  horoscope: {
    title: 'Daily Horoscope & Astro Vastu Remedies | Zodiac Solutions | Astro Vastu Remedies',
    description: 'Get daily horoscope with Astro Vastu remedies. Personalized solutions for money, career, health problems based on zodiac. Expert guidance by Astro Vastu Remedies.',
    keywords: 'daily horoscope vastu, zodiac vastu remedies, rashifal with remedies, astro vastu predictions, horoscope solutions india',
  },
  matchmaking: {
    title: 'Kundli Matching | Astro Vastu for Marriage | Dosha Remedies | Astro Vastu Remedies',
    description: 'Free Kundli matching with Astro Vastu analysis. Get Manglik dosha remedy, marriage compatibility solutions. Expert consultation by Astro Vastu Remedies.',
    keywords: 'kundli matching vastu, marriage astro vastu, manglik dosha remedy, marriage vastu consultation, compatibility astro vastu',
  },
  panchang: {
    title: 'Today Panchang | Shubh Muhurat for Vastu | Astro Vastu Remedies',
    description: 'Get today\'s Panchang with auspicious timings for Vastu corrections and remedies. Best muhurat for home, business, career activities.',
    keywords: 'panchang vastu muhurat, shubh muhurat vastu, auspicious time vastu, daily panchang remedies',
  },
  numerology: {
    title: 'Numerology & Astro Vastu | Lucky Numbers for Vastu | Astro Vastu Remedies',
    description: 'Combine numerology with Astro Vastu for powerful remedies. Lucky numbers for home, business. Name correction with Vastu analysis.',
    keywords: 'numerology vastu, lucky numbers vastu, name correction astro vastu, number remedies vastu',
  },
  tarot: {
    title: 'Tarot Reading | Spiritual Guidance | Astro Vastu Remedies',
    description: 'Free tarot reading with Astro Vastu guidance. Get spiritual solutions for life problems. Expert remedies for love, career challenges.',
    keywords: 'tarot astro vastu, spiritual guidance india, tarot remedies, divine guidance vastu',
  },
  loveCalculator: {
    title: 'Love Compatibility | Vastu Remedies for Relationship | Astro Vastu Remedies',
    description: 'Check love compatibility and get Vastu remedies for relationship problems. Astro Vastu solutions for love marriage, partner issues.',
    keywords: 'love vastu remedies, relationship vastu, marriage vastu solutions, partner problem vastu remedy',
  },
  dosha: {
    title: 'Dosha Analysis & Astro Vastu Remedies | Manglik Kaal Sarp Solutions | Pinnacle Vastu',
    description: 'Complete dosha analysis with Astro Vastu remedies. Manglik, Kaal Sarp, Pitra dosha solutions by Astro Vastu Remedies. Expert Vastu corrections.',
    keywords: 'manglik dosha vastu remedy, kaal sarp vastu remedy, pitra dosha vastu, dosha nivaran astro vastu, sade sati vastu remedy',
  },
  gemRecommendation: {
    title: 'Gemstone Recommendation | Astro Vastu Gemstones | Pinnacle Vastu Remedies',
    description: 'Get gemstone recommendations based on Kundli and Vastu. Planet-based gemstones for home and personal use. Expert guidance by Astro Vastu Remedies.',
    keywords: 'gemstone vastu, planet gemstone remedy, astro vastu gemstones, vastu gemstone consultation, pinnacle vastu gemstones',
  },
  dasha: {
    title: 'Dasha Analysis | Astro Vastu Remedies for Mahadasha | Pinnacle Vastu',
    description: 'Know your Mahadasha with Astro Vastu remedies. Planet dasha solutions for home and business. Expert guidance by Astro Vastu Remedies.',
    keywords: 'dasha vastu remedies, mahadasha astro vastu, planet dasha solutions, saturn dasha vastu remedy',
  },
  vastuCompass: {
    title: 'Vastu Compass | 16 Zone Astro Vastu Analysis | Astro Vastu Remedies',
    description: 'Interactive Vastu compass for 16 zone analysis. Get Astro Vastu remedies for each direction. Expert solutions for money, health, career problems.',
    keywords: 'vastu compass, 16 zone vastu, astro vastu analysis, direction vastu remedies, zone wise vastu solutions, pinnacle vastu compass',
  },
  vastuTools: {
    title: 'Astro Vastu Tools | Pinnacle Vastu Analysis | Astro Vastu Remedies',
    description: 'Professional Astro Vastu analysis tools. Zone-wise solutions for home, office, factory. MahaVastu remedies without demolition by Pinnacle Vastu.',
    keywords: 'astro vastu tools, pinnacle vastu analysis, vastu without demolition, mahavastu remedies, astro vastu solutions india',
  },
  about: {
    title: 'About Astro Vastu Remedies | Astro Vastu Remedies | Pinnacle Vastu Expert',
    description: 'Learn about Astro Vastu Remedies by Astro Vastu Remedies. Top Vastu consultant in India. Expert Astro Vastu consultation for home, business, career.',
    keywords: 'pinnacle vastu, astro vastu expert india, top vastu consultant india, best astro vastu consultant',
  },
  contact: {
    title: 'Contact Astro Vastu Consultation | Astro Vastu Remedies | Online Vastu India',
    description: 'Get in touch for Astro Vastu consultation with Astro Vastu Remedies. Online Vastu consultation India. Expert solutions for money, health, career problems.',
    keywords: 'astro vastu consultation, online vastu consultation india, contact vastu expert, pinnacle vastu consultation',
  },
  privacyPolicy: {
    title: 'Privacy Policy | Astro Vastu Remedies | Pinnacle Vastu',
    description: 'Read our privacy policy to understand how we collect, use, and protect your personal information at Astro Vastu Remedies.',
  },
  termsConditions: {
    title: 'Terms & Conditions | Astro Vastu Remedies | Pinnacle Vastu',
    description: 'Review the terms and conditions for using Astro Vastu Remedies website and services.',
  },
  refundPolicy: {
    title: 'Refund Policy | Astro Vastu Remedies | Pinnacle Vastu',
    description: 'Learn about our refund and return policy for products purchased from Astro Vastu Remedies.',
  },
  shippingPolicy: {
    title: 'Shipping Policy | Astro Vastu Remedies',
    description: 'Information about shipping methods, delivery times, and charges for Astro Vastu Remedies products.',
  },
};

// Category-specific SEO for product filtering
export const categorySEO = {
  'bracelets': {
    title: 'Healing Bracelets | Gemstone & Crystal Remedy Bracelets | Astro Vastu Remedies',
    description: 'Shop powerful healing bracelets with gemstones & crystals. Remedy bracelets for money, health, protection & success. Energized by expert astrologers.',
    keywords: 'healing bracelets, gemstone bracelet, crystal bracelet, money bracelet, protection bracelet, health bracelet, remedy bracelet',
  },
  'rudraksha': {
    title: 'Original Rudraksha | Mukhi Rudraksha Remedies for Health & Success | Astro Vastu Remedies',
    description: 'Buy certified original Rudraksha beads. 1 to 21 Mukhi Rudraksha remedies for health problems, success, peace & spiritual growth. Lab tested with certificate.',
    keywords: 'original rudraksha, mukhi rudraksha, rudraksha benefits, rudraksha for health, rudraksha remedy, ek mukhi rudraksha, panchmukhi rudraksha, rudraksha mala',
  },
  'gemstones': {
    title: 'Certified Gemstones | Planet Remedy Stones | Astro Vastu Remedies',
    description: 'Buy certified natural gemstones for astrological remedies. Ruby, Pearl, Coral, Emerald, Yellow Sapphire, Diamond, Blue Sapphire, Hessonite & Cat\'s Eye for planet remedies.',
    keywords: 'certified gemstones, planet gemstones, ruby stone, pearl stone, coral stone, emerald, yellow sapphire, blue sapphire, hessonite, cats eye, navratna',
  },
  'yantras': {
    title: 'Powerful Yantras | Yantra Remedies for Wealth & Protection | Astro Vastu Remedies',
    description: 'Shop energized yantras for wealth, protection & success. Sri Yantra, Kuber Yantra, Shree Yantra, Vastu Yantra & more. Copper & gold plated yantras with puja vidhi.',
    keywords: 'yantra remedies, sri yantra, kuber yantra, shree yantra, vastu yantra, yantra for money, yantra for protection, energized yantra, copper yantra',
  },
  'crystal-trees': {
    title: 'Crystal Trees | Feng Shui Money & Healing Trees | Astro Vastu Remedies',
    description: 'Buy crystal trees for wealth, health & positive energy. Citrine money tree, amethyst healing tree, rose quartz love tree. Vastu & Feng Shui approved.',
    keywords: 'crystal tree, money tree, feng shui tree, citrine tree, amethyst tree, rose quartz tree, healing crystal tree, vastu tree',
  },
  'malas': {
    title: 'Prayer Malas | Japa Mala for Mantra & Meditation | Astro Vastu Remedies',
    description: 'Shop authentic prayer malas for japa & meditation. Rudraksha mala, crystal mala, sandalwood mala, tulsi mala. 108 beads mala with proper energization.',
    keywords: 'japa mala, prayer mala, rudraksha mala, crystal mala, meditation mala, 108 beads mala, mantra mala, rosary beads',
  },
  'vedic-astrologic-remedies': {
    title: 'Vedic Astrology Remedies | Graha Shanti Products | Astro Vastu Remedies',
    description: 'Complete Vedic astrology remedy products. Graha shanti items, planet puja samagri, dosha nivaran products. Everything for astrological remedies.',
    keywords: 'vedic remedies, graha shanti, planet remedies, dosha nivaran, puja samagri, astrology remedy products, graha dosh remedy',
  },
  '3d-remedies': {
    title: '3D Vastu Remedies | Modern Vastu Correction Products | Astro Vastu Remedies',
    description: 'Innovative 3D Vastu remedy products for modern homes. Vastu correction without demolition using advanced remedy techniques.',
    keywords: '3d vastu remedies, modern vastu products, vastu correction, no demolition vastu, vastu solution products',
  },
  'treatment-remedies': {
    title: 'Healing & Treatment Remedies | Health Problem Solutions | Astro Vastu Remedies',
    description: 'Spiritual healing and treatment remedies for health problems. Energy healing products, chakra balancing, aura cleansing items.',
    keywords: 'healing remedies, treatment remedies, health remedies, chakra healing, aura cleansing, energy healing, spiritual healing products',
  },
};

// Comprehensive FAQ Schema for Remedies
export const remedyFAQs = [
  {
    question: 'Which gemstone should I wear for success and money?',
    answer: 'For success and money, Yellow Sapphire (Pukhraj) for Jupiter and Emerald (Panna) for Mercury are highly recommended. However, the right gemstone depends on your birth chart. A weak Jupiter needs Yellow Sapphire, while a weak Mercury needs Emerald. Always consult your Kundli before wearing any gemstone.',
  },
  {
    question: 'How to remove Vastu dosha from home without demolition?',
    answer: 'Vastu dosha can be corrected without demolition using remedies like Vastu pyramids, Vastu yantras, specific colors, mirrors, and plants in affected zones. MahaVastu remedies focus on balancing the 16 zones using symbolic corrections rather than structural changes.',
  },
  {
    question: 'What is the remedy for Manglik dosha in marriage?',
    answer: 'Manglik dosha remedies include: 1) Kumbh Vivah (symbolic marriage with a pot), 2) Wearing Red Coral gemstone, 3) Mangal Shanti Puja, 4) Chanting Hanuman Chalisa, 5) Marrying another Manglik person. The specific remedy depends on the severity and placement of Mars in your chart.',
  },
  {
    question: 'Which Rudraksha is best for health problems?',
    answer: 'For general health, 5 Mukhi Rudraksha is most beneficial as it balances all five elements. For heart problems, 1 Mukhi or 12 Mukhi is recommended. For blood pressure, 11 Mukhi helps. For mental health and stress, 6 Mukhi Rudraksha is effective. Always wear after proper energization.',
  },
  {
    question: 'How to do Kaal Sarp Dosha remedy at home?',
    answer: 'Kaal Sarp Dosha home remedies include: 1) Chanting "Om Namah Shivaya" 108 times daily, 2) Offering milk to Shivling on Mondays, 3) Keeping a silver snake idol, 4) Wearing Gomed (Hessonite) or Cat\'s Eye gemstone, 5) Donating to snake-related causes on Nag Panchami.',
  },
  {
    question: 'What yantra should I keep for money and wealth?',
    answer: 'For money and wealth, Shree Yantra and Kuber Yantra are most powerful. Shree Yantra attracts abundance and prosperity, while Kuber Yantra specifically activates wealth. Place them in the North or Northeast direction, energize with proper mantras, and worship regularly on Fridays.',
  },
  {
    question: 'How to reduce Sade Sati effects?',
    answer: 'Sade Sati remedies include: 1) Wearing Blue Sapphire or Amethyst (after Kundli check), 2) Chanting Shani Mantra or Hanuman Chalisa on Saturdays, 3) Donating black items on Saturdays, 4) Keeping Shani Yantra, 5) Lighting mustard oil lamp under Peepal tree on Saturday evenings.',
  },
  {
    question: 'Which crystal is best for positive energy at home?',
    answer: 'Clear Quartz is the master healer for overall positive energy. Amethyst provides spiritual protection and peace. Black Tourmaline absorbs negative energy. Citrine attracts wealth and success. Rose Quartz enhances love and relationships. Place them in appropriate Vastu zones for best results.',
  },
  {
    question: 'What is the remedy for Pitra Dosha?',
    answer: 'Pitra Dosha remedies include: 1) Performing Shraddha rituals during Pitru Paksha, 2) Tarpan for ancestors, 3) Donating food to Brahmins on Amavasya, 4) Pind Daan at holy places, 5) Feeding crows daily, 6) Keeping ancestors\' photos in South-West and offering prayers. Regular ancestor worship reduces this dosha.',
  },
  {
    question: 'How to choose the right gemstone based on Kundli?',
    answer: 'Gemstone selection is based on: 1) Ascendant Lord - wear its gemstone for overall strength, 2) Weak benefic planets in chart need support, 3) Mahadasha planet - strengthen current ruling planet, 4) 9th or 5th house lords for luck. Never wear gemstones of malefic planets for your chart. Always consult an expert astrologer.',
  },
];

// SEO Component
export const SEO = ({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  schema,
  canonicalUrl,
  price,
  currency = 'INR',
  availability,
}) => {
  const seoTitle = title || defaultSEO.title;
  const seoDescription = description || defaultSEO.description;
  const seoKeywords = keywords || defaultSEO.keywords;
  const seoImage = toAbsoluteUrl(image);
  const seoUrl = url || SITE_URL;
  const canonical = canonicalUrl || seoUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:secure_url" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="1200" />
      <meta property="og:image:alt" content={seoTitle} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Product-specific Open Graph (rich price previews) */}
      {type === 'product' && price != null && (
        <meta property="product:price:amount" content={String(price)} />
      )}
      {type === 'product' && price != null && (
        <meta property="product:price:currency" content={currency} />
      )}
      {type === 'product' && availability && (
        <meta property="product:availability" content={availability} />
      )}
      {type === 'product' && price != null && (
        <meta property="og:price:amount" content={String(price)} />
      )}
      {type === 'product' && price != null && (
        <meta property="og:price:currency" content={currency} />
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Additional Meta Tags */}
      <meta name="author" content={AUTHOR} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="language" content="English" />
      <meta name="geo.position" content="28.6139;77.2090" />
      <meta name="ICBM" content="28.6139, 77.2090" />
      
      {/* Schema.org JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

// Product SEO Component
export const ProductSEO = ({ product }) => {
  if (!product) return null;

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: toAbsoluteUrl(product.image),
    url: `${SITE_URL}/product/${product.id}`,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'INR',
      availability: product.inStock 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
    },
    aggregateRating: product.rating ? {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviews || 100,
      bestRating: 5,
      worstRating: 1,
    } : undefined,
  };

  return (
    <SEO
      title={`${product.name} | Buy Online | ${SITE_NAME}`}
      description={product.description?.substring(0, 160) || `Buy ${product.name} online at best price. Authentic and energized by certified astrologers. Fast delivery across India.`}
      keywords={`${product.name}, buy ${product.name}, ${product.name} price, spiritual products`}
      image={toAbsoluteUrl(product.image)}
      url={`${SITE_URL}/product/${product.id}`}
      type="product"
      price={product.price}
      currency="INR"
      availability={product.inStock ? 'in stock' : 'out of stock'}
      schema={productSchema}
    />
  );
};

// Organization Schema (for homepage) - Astro Vastu Remedies / Pinnacle Vastu
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Astro Vastu Remedies - Pinnacle Vastu',
  alternateName: 'Pinnacle Vastu by Astro Vastu Remedies',
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description: 'Get powerful Astro Vastu remedies based on astrology and vastu principles. Solve problems related to money, health, career and relationships with expert consultation by Astro Vastu Remedies.',
  founder: {
    '@type': 'Person',
    name: 'Astro Vastu Remedies',
    jobTitle: 'Astro Vastu Expert',
    description: 'Top Vastu consultant in India specializing in Astro Vastu remedies for home, business, career, and relationship problems.',
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
    addressRegion: 'India',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'support@astrovasturemedies.in',
    availableLanguage: ['English', 'Hindi'],
  },
  sameAs: [
    'https://www.facebook.com/AstroVastuRemedies',
    'https://www.instagram.com/astro_vasturemedies',
  ],
  knowsAbout: [
    'Astro Vastu',
    'Astro Vastu Remedies',
    'Astro Vastu Consultation',
    'Vastu Remedies for Money Problems',
    'Vastu Remedies for Business Loss',
    'Vastu Remedies for Career Growth',
    'Vastu Remedies for Health Problems',
    'Vastu Remedies for Relationship Issues',
    'Vastu Correction Without Demolition',
    'Kundli Based Vastu Remedies',
    'Planet Based Vastu Remedies',
    'Pinnacle Vastu',
  ],
};

// WebSite Schema (for search box)
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/products?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

// E-commerce Store Schema - Astro Vastu focused
export const storeSchema = {
  '@context': 'https://schema.org',
  '@type': 'Store',
  name: 'Astro Vastu Remedies - Pinnacle Vastu',
  url: SITE_URL,
  image: `${SITE_URL}/logo.png`,
  priceRange: '₹₹',
  email: 'support@astrovasturemedies.in',
  telephone: '+91-95011-47546',
  description: 'Online Astro Vastu consultation and remedy products store by Astro Vastu Remedies. Vastu remedies for money problems, business loss, career growth, health problems. Shop yantras, gemstones, rudraksha for Vastu correction without demolition.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'IN',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '28.6139',
    longitude: '77.2090',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    opens: '00:00',
    closes: '23:59',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Astrology & Vastu Remedy Products',
    itemListElement: [
      {
        '@type': 'OfferCatalog',
        name: 'Gemstone Remedies',
        description: 'Certified gemstones for planetary remedies',
      },
      {
        '@type': 'OfferCatalog',
        name: 'Rudraksha',
        description: 'Original Rudraksha beads for health and spiritual benefits',
      },
      {
        '@type': 'OfferCatalog',
        name: 'Yantras',
        description: 'Energized yantras for wealth, protection and success',
      },
      {
        '@type': 'OfferCatalog',
        name: 'Vastu Products',
        description: 'Vastu correction products and remedies',
      },
    ],
  },
};

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (items) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

// FAQ Schema Generator
export const generateFAQSchema = (faqs) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

// Service Schema (for astrology services)
export const generateServiceSchema = (service) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    serviceType: 'Astrology Services',
    areaServed: {
      '@type': 'Country',
      name: 'India',
    },
  };
};

export default SEO;
